import React from 'react';
import {
  Accordion,
  Divider,
  Heading,
  IconBulb,
  IconCheckmarkCircle,
  IconExclamationCircle,
  mediaBreakpointMixin,
  Tag,
} from '@zillow/constellation';
import { ApplicationOverviewStep } from './ApplicationOverviewStep';
import type { Answers } from '../../../graphql/queries';
import { getOverviewFromApplication } from '../../../utils/overview/overview-statuses';
import { useRouter } from 'next/router';
import { getEditRoute } from '../../../constants/urls';
import { FIELD_IDS, REVIEW_STATUS } from '../../../constants/wizard';
import { pluralize } from '../../../utils/formatters';
import styled from 'styled-components';
import { useApplicationContext } from '../../../contexts/ApplicationContext';
import { useFormContext } from 'react-hook-form';

interface Props {
  isReadOnly?: boolean;
}

const StyledDiv = styled.div`
  display: none;
  @media ${mediaBreakpointMixin('md')} {
    display: block;
  }
`;

const StyledSpan = styled.span`
  display: block;
  @media ${mediaBreakpointMixin('md')} {
    display: none;
  }
`;

export function ApplicationOverviewAccordion({ isReadOnly }: Props) {
  const router = useRouter();
  const applicationContext = useApplicationContext();
  const { watch } = useFormContext() ?? {};
  const overview = getOverviewFromApplication(applicationContext, watch?.(FIELD_IDS.REQUEST_ZILLOW_PAYMENTS));

  const { application, userStatuses } = applicationContext;
  const applicationId = application.id as string;
  const answers = application.answers as Answers;

  function onClickEdit(stepId: string) {
    router.push(getEditRoute({ applicationId, stepId }));
  }

  return (
    <Accordion>
      {overview.sections.map((section) => {
        return (
          <Accordion.Item key={section.label}>
            <Accordion.Header>
              <Heading level="5" as="h3">
                {section.label}
              </Heading>
              {!isReadOnly && (
                <>
                  {section.status === REVIEW_STATUS.COMPLETE && !!section.recommendations && (
                    <>
                      <StyledSpan>
                        <IconBulb
                          fontColor="blue600"
                          aria-label={pluralize('recommendation', section.recommendations)}
                        />
                      </StyledSpan>
                      <StyledDiv>
                        <Tag appearance="info" textTransform="none" icon={<IconBulb />}>
                          {pluralize('recommendation', section.recommendations)}
                        </Tag>
                      </StyledDiv>
                    </>
                  )}
                  {section.status === REVIEW_STATUS.COMPLETE && !section.recommendations && (
                    <>
                      <StyledSpan>
                        <IconCheckmarkCircle fontColor="green600" aria-label="Complete" />
                      </StyledSpan>
                      <StyledDiv>
                        <Tag appearance="success" textTransform="none" icon={<IconCheckmarkCircle />}>
                          Complete
                        </Tag>
                      </StyledDiv>
                    </>
                  )}
                  {section.status === REVIEW_STATUS.INCOMPLETE && (
                    <>
                      <StyledSpan>
                        <IconExclamationCircle fontColor="yellow600" aria-label="Incomplete" />
                      </StyledSpan>
                      <StyledDiv>
                        <Tag appearance="warning" textTransform="none" icon={<IconExclamationCircle />}>
                          Incomplete
                        </Tag>
                      </StyledDiv>
                    </>
                  )}
                  {section.status === REVIEW_STATUS.NOT_STARTED && (
                    <>
                      <StyledSpan>
                        <IconExclamationCircle fontColor="gray600" aria-label="Start now" />
                      </StyledSpan>
                      <StyledDiv>
                        <Tag appearance="gray" textTransform="none" icon={<IconExclamationCircle />}>
                          Start now
                        </Tag>
                      </StyledDiv>
                    </>
                  )}
                </>
              )}
            </Accordion.Header>
            <Accordion.Panel>
              {section.steps.map((step, index) => {
                return (
                  <React.Fragment key={step.label}>
                    <ApplicationOverviewStep
                      applicationId={applicationId}
                      isPrimaryApplicant={!!application.isPrimaryApplicant}
                      step={step}
                      answers={answers}
                      userStatuses={userStatuses}
                      onClickEdit={() => onClickEdit(step.id)}
                      isReadOnly={isReadOnly}
                    />
                    {index < section.steps.length - 1 && <Divider marginY="md" marginLeft="lg" />}
                  </React.Fragment>
                );
              })}
            </Accordion.Panel>
          </Accordion.Item>
        );
      })}
    </Accordion>
  );
}
